// src/lib/contrat-vars.js — LOT A (moteur contrat templatable) : catalogue +
// résolveur des variables {var} et drapeaux {#if flag} des CLAUSES du contrat.
//
// ⚠️ MIROIR ESM de lib/contrat-vars.js (CJS serveur — le CANONIQUE, voir son
// cartouche pour la doc complète). Le CORPS de ce fichier (tout sauf ce
// cartouche, les imports et les exports) est une COPIE EXACTE du CJS. NE PAS
// éditer l'un sans l'autre (duplication contrôlée ADR-021 — CRA interdit
// l'import racine `lib/` depuis `src/`). Parité prouvée par
// src/lib/contrat-vars.parity.test.js.

// Fenêtre J-2 caution (ADR-174) — SOURCE UNIQUE partagée avec le serveur
// (src/lib/caution-window.js, F10 phase 2) : plus de valeur recopiée ici.
import { JOURNEY_CAUTION_WINDOW_DAYS } from './caution-window';
// {logement_nom} : règle UNIQUE partagée avec les factures (30/09).
import { nomLogementFacture } from './nom-logement-facture';
// CONTRAT V2 (art. 6) — SOURCE UNIQUE du mode caution empreinte/débit (ADR-174,
// chemin argent api/stripe.js), partagée avec le serveur depuis F10 phase 2 :
// le seuil et la fonction ne sont plus recopiés ici.
// P-2.56 (30/10) : montant, seuil et mode viennent de LA porte cautionPourSejour
// (src/lib/caution-figee.js) : caution figée de la résa si elle la porte, sinon
// le réglage du logement pour la durée — la même que lit api/stripe.js.
import { cautionPourSejour } from './caution-figee';
// Moteur {#if} partagé (résolveur INJECTABLE, cf. PARTIE B ADR-272) : MÊME
// syntaxe et même sémantique de vérité que les emails Direct/Plateformes.
import { evaluateConditions } from './template-variables';

// ── {clause_annulation} — paragraphes juridiques CONTRAT (article 11.1) ──────
//
// Textes EXACTS de docs/handoff/contrat-location-saisonniere-v2.md § 11.1
// (blockquotes FLEXIBLE / MODÉRÉE / STRICTE / NON REMBOURSABLE / CUSTOM),
// marqueurs d'emphase markdown retirés (les clauses sont du texte brut échappé —
// des `**` littéraux sortiraient tels quels dans le PDF).
//
// ⚠️ DISTINCT des textes emails (lib/cancellation-policy.js DEFAULT_POLICIES +
// overrides KV) : le contrat porte SES paragraphes juridiques figés. Un override
// KV du texte email d'un des 4 enums NE change PAS la clause contrat — seule la
// SÉLECTION (enum / custom / repli) est le miroir décisionnel de resolvePolicyText.
const CONTRAT_CLAUSE_ANNULATION_TEXTS = {
  flexible:
    "En cas d'annulation par le Voyageur au plus tard quarante-huit (48) heures avant l'heure d'arrivée prévue, les sommes versées lui sont intégralement remboursées. Passé ce délai, ou en cas de non-présentation, l'intégralité des sommes versées demeure acquise au Loueur à titre d'indemnité forfaitaire.",
  moderee:
    "En cas d'annulation par le Voyageur au plus tard cinq (5) jours avant la date d'arrivée, cinquante pour cent (50 %) des sommes versées lui sont remboursées. Passé ce délai, ou en cas de non-présentation, l'intégralité des sommes versées demeure acquise au Loueur à titre d'indemnité forfaitaire.",
  stricte:
    "En cas d'annulation par le Voyageur au plus tard trente (30) jours avant la date d'arrivée, cinquante pour cent (50 %) des sommes versées lui sont remboursées. Passé ce délai, ou en cas de non-présentation, la réservation est non remboursable et l'intégralité des sommes versées demeure acquise au Loueur à titre d'indemnité forfaitaire.",
  non_remboursable:
    "La réservation est non remboursable. En cas d'annulation par le Voyageur, quelle qu'en soit la cause, ou en cas de non-présentation, l'intégralité des sommes versées demeure acquise au Loueur à titre d'indemnité forfaitaire.",
};

// Miroirs de lib/cancellation-policy.js (POLICY_ENUMS + CUSTOM_KEY_RE) — inlinés
// pour garder ce module PUR et importable côté ESM (parité testée).
const CONTRAT_POLICY_ENUMS = ['flexible', 'moderee', 'stricte', 'non_remboursable'];
const CONTRAT_CUSTOM_POLICY_KEY_RE = /^custom_[a-z0-9_]{1,46}$/;

function _nonEmptyStr(v) {
  return typeof v === 'string' && v.trim() !== '';
}

/**
 * Résout le paragraphe juridique {clause_annulation} du CONTRAT — sélection
 * enum/custom miroir de resolvePolicyText (lib/cancellation-policy.js), mais
 * STRICTE (décision Dimitri 2026-08-01) : PLUS AUCUN repli silencieux sur
 * NON REMBOURSABLE — appliquer au voyageur une clause plus dure que celle de
 * son logement est juridiquement contestable. Cas :
 *   - policyKey ABSENT (null/'' — logement sans politique posée) → RÉSOLU
 *     'non_remboursable' : c'est le DÉFAUT DB de logement_site_content
 *     (politique_annulation DEFAULT 'non_remboursable'), pas un repli ;
 *   - un des 4 enums → RÉSOLU, son paragraphe juridique (article 11.1) ;
 *   - clé custom présente au blob avec text_fr non vide → RÉSOLU, paragraphe
 *     custom ;
 *   - clé custom absente/illisible → NON-RÉSOLU { ok:false, reason:
 *     'custom_policy_missing' } ;
 *   - toute autre valeur (enum inconnu, type inattendu) → NON-RÉSOLU
 *     { ok:false, reason:'unknown_policy_enum' }.
 * Le call-site serveur (fetchContratClauseAnnulation) traduit NON-RÉSOLU en
 * « contrat non généré + alerte admin » (jamais de contrat avec une clause
 * devinée). JAMAIS de throw.
 * @param {string|null|undefined} policyKey — logement_site_content.politique_annulation
 * @param {object|null|undefined} overrides — blob KV fbz8-cancellation-policies
 * @returns {{ok:true, text:string}|{ok:false, reason:string}}
 */
function resolveContratClauseAnnulation(policyKey, overrides) {
  if (policyKey == null || policyKey === '') {
    // Défaut DB : logement sans politique posée = non_remboursable ASSUMÉ.
    return { ok: true, text: CONTRAT_CLAUSE_ANNULATION_TEXTS.non_remboursable };
  }
  if (typeof policyKey === 'string' && CONTRAT_CUSTOM_POLICY_KEY_RE.test(policyKey)) {
    if (
      overrides
      && typeof overrides === 'object'
      && Object.prototype.hasOwnProperty.call(overrides, policyKey)
      && overrides[policyKey]
      && typeof overrides[policyKey] === 'object'
      && _nonEmptyStr(overrides[policyKey].text_fr)
    ) {
      return {
        ok: true,
        text:
          "Les conditions d'annulation applicables au présent séjour, portées à la connaissance du Voyageur avant la réservation, sont les suivantes : « "
          + overrides[policyKey].text_fr.trim()
          + ' »',
      };
    }
    return { ok: false, reason: 'custom_policy_missing' };
  }
  if (typeof policyKey === 'string' && CONTRAT_POLICY_ENUMS.includes(policyKey)) {
    return { ok: true, text: CONTRAT_CLAUSE_ANNULATION_TEXTS[policyKey] };
  }
  return { ok: false, reason: 'unknown_policy_enum' };
}

// ── Catalogue des variables contrat ──────────────────────────────────────────
//
// TOKENS = rendu texte (et utilisables en {#if token} — vérité = non vide).
// FLAGS  = conditions pures {#if flag} ('1' quand vrai, '' sinon).
const CONTRAT_TOKEN_NAMES = [
  'proprietaire_nom',
  'proprietaire_adresse',
  'proprietaire_email',
  'proprietaire_telephone',
  'voyageur_nom',
  'voyageur_email',
  'voyageur_telephone',
  'logement_nom',
  'logement_adresse',
  'logement_composition',
  'logement_capacite',
  'date_arrivee',
  'date_depart',
  'heure_arrivee',
  'heure_depart',
  'nombre_nuits',
  'montant_hebergement',
  'montant_menage',
  'montant_taxe',
  'montant_total',
  'montant_caution',
  'jours_avant_caution',
  'date_signature_loueur',
  'clause_annulation',
  // CONTRAT V2 — seuil empreinte/débit SERVI par lib/caution-mode.js (jamais un
  // chiffre en dur dans un texte juridique) + médiateur de la consommation
  // (art. 16, loueur professionnel uniquement — cfg.co.mediateur_* / env).
  'seuil_nuits_caution',
  'mediateur_nom',
  'mediateur_coordonnees',
];
// CONTRAT V2 — drapeaux additionnels :
//   - loueur_professionnel : émetteur FBZ SAS (isSousLoc p6 Maison Zen) — arme
//     le paragraphe médiateur de l'art. 16 (obligation pro L. 612-1 C. conso).
//   - caution_due / caution_absente : jumeaux EXCLUSIFS dérivés du verdict
//     matrice `cautionDue` (cautionHidden) — un contrat sans caution ne parle
//     JAMAIS de dépôt de garantie (art. 4.3, 5.2, 6, 7.4, 8.3).
//   - caution_empreinte / caution_debit : jumeaux EXCLUSIFS dérivés de
//     cautionModeForNights(nb nuits) — le contrat dit EXACTEMENT ce que Stripe
//     fera (empreinte = hold jamais débité · débit réel = prélevé puis
//     remboursé). Tous deux '' quand caution_absente.
//   - nuits_connues : nombre de nuits dérivable des dates de la résa — gate
//     les affirmations factuelles de durée (phrase de seuil art. 6) sur un
//     contrat type aux dates en blancs (revue m9).
const CONTRAT_FLAG_NAMES = [
  'animaux_non',
  'fumeur_non',
  'loueur_professionnel',
  'caution_due',
  'caution_absente',
  'caution_empreinte',
  'caution_debit',
  'nuits_connues',
];
const CONTRAT_VAR_NAMES = [...CONTRAT_TOKEN_NAMES, ...CONTRAT_FLAG_NAMES];

// Décision Dimitri 2026-08-01 — DEUX comportements pour {clause_annulation} :
//   - VOYAGEUR (contract_read / contract_sign / contract-pdf) = FAIL-CLOSED :
//     clause non résolue → le contrat NE SE GÉNÈRE PAS (503 + alerte admin,
//     cf. fetchContratClauseAnnulation). Jamais de clause devinée dans un
//     document signé.
//   - APERÇU ADMIN (ContratTab / ResaV2Modal, client sans accès serveur à la
//     politique) = PERMISSIF VISIBLE : le token rend ce PLACEHOLDER
//     d'avertissement — l'admin doit VOIR le problème, pas être bloqué.
// Ce placeholder ne peut atteindre un document voyageur par les chemins
// serveur (qui refusent avant rendu) ; seul l'envoi manuel admin historique
// (ContratTab « Envoyer au voyageur », HTML généré client) pourrait le
// contenir — il est alors visible dans l'aperçu AVANT envoi.
const CONTRAT_CLAUSE_ANNULATION_PLACEHOLDER =
  "⚠️ Clause d'annulation non résolue — aperçu admin : la clause réelle (politique d'annulation du logement) est insérée à la génération serveur du contrat. Si ce texte apparaît dans un document voyageur, prévenir l'administrateur.";

// Blancs « à compléter à la main » — mêmes conventions que le contrat historique
// (contrat type sans résa : identités longues, montants courts, dates ____/…).
const BLANK_LONG = '________________________________';
const BLANK_AMOUNT = '________';
const BLANK_SHORT = '____';

/**
 * Règle logement en BOOLÉEN STRICT — miroir de _cfgRuleBoolStrict (api/sites.js,
 * PARTIE B ADR-272) : une clé ABSENTE ne vaut PAS « non autorisé » (une clause
 * qui AFFIRME « animaux interdits » sur une config muette serait une affirmation
 * fausse dans un document signé). Cascade self → parentLog (sous-loc lc1/lc2/lc3).
 * @returns {boolean|undefined}
 */
function _contratRuleBoolStrict(log, ...keys) {
  for (const source of [log, log && log.parentLog]) {
    if (!source || typeof source !== 'object') continue;
    for (const k of keys) {
      if (typeof source[k] === 'boolean') return source[k];
    }
  }
  return undefined;
}

// Montant → '450.00' (même format que le contrat historique, Number.toFixed(2)),
// ou null si la valeur n'est pas un nombre fini.
function _fmtMontant(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : null;
}

// Nombre de nuits DÉRIVÉ des dates ISO (jamais un champ saisi). '' si invalide.
function _nightsCount(startIso, endIso) {
  if (!startIso || !endIso) return '';
  const s = new Date(startIso);
  const e = new Date(endIso);
  const diff = e - s;
  if (Number.isNaN(diff) || diff <= 0) return '';
  return String(Math.round(diff / 86400000));
}

/**
 * ISO datetime → 'JJ/MM/AAAA à HHhMM' en Europe/Paris (miroir de
 * formatDateTimeFr, lib/direct-vars-catalog.js). Entrée invalide → ''.
 * Sert la ligne « signature voyageur datée » du rendu post-signature.
 * @param {string} iso
 * @returns {string}
 */
function formatSignatureDateTimeFr(iso) {
  if (!_nonEmptyStr(iso)) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('fr-FR', {
      timeZone: 'Europe/Paris',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(d);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value || '';
    return `${get('day')}/${get('month')}/${get('year')} à ${get('hour')}h${get('minute')}`;
  } catch (_) {
    return '';
  }
}

/**
 * Construit la map de variables du contrat. Les valeurs déjà calculées par
 * genContratHTML (dates, identité voyageur, horaires, caution, adresse) sont
 * PASSÉES telles quelles — jamais recalculées ici — pour que les clauses disent
 * EXACTEMENT la même chose que l'en-tête/récap du document.
 *
 * @param {object} args
 * @param {object} args.emitter        — resolveEmitter({cfg, prop})
 * @param {object} args.log            — logement aplati (peut porter parentLog)
 * @param {object|null} args.reservation — résa/demande (ou null : contrat type)
 * @param {string} args.dateEmission   — date d'émission fr-FR
 * @param {string} args.voyageurNom / voyageurEmail / voyageurTel
 * @param {string} args.dateArrivee / dateDepart — déjà formatées fr-FR (ou blancs)
 * @param {string} args.checkinTime / checkoutTime
 * @param {*} args.cautionValue        — montant caution résolu multi-sources
 * @param {string} args.adressePublique — adresse contrat (Q8 ADR-063)
 * @param {string|null} [args.clauseAnnulation] — paragraphe 11.1 PRÉ-RÉSOLU
 *   CÔTÉ SERVEUR (fetchContratClauseAnnulation). Absent/vide → repli NON
 *   REMBOURSABLE (jamais de contrat muet sur l'annulation).
 * @param {boolean} [args.cautionHidden] — verdict matrice PRÉ-RÉSOLU par
 *   genContratHTML (cautionDue === false). true → caution_absente ; sinon
 *   caution_due + mode empreinte/débit (rendu historique conservé).
 * @param {string} [args.mediateurNom] / [args.mediateurCoordonnees] — médiateur
 *   de la consommation (art. 16), résolus par genContratHTML depuis
 *   cfg.co.mediateur_* (fallback env MEDIATEUR_NOM / MEDIATEUR_COORDONNEES).
 *   Absents → '' → le paragraphe médiateur ne se rend PAS ({#if … ET …}).
 * @returns {Object<string,string>}
 */
function buildContratVars({
  emitter,
  log,
  reservation,
  dateEmission,
  voyageurNom,
  voyageurEmail,
  voyageurTel,
  dateArrivee,
  dateDepart,
  checkinTime,
  checkoutTime,
  cautionValue,
  adressePublique,
  clauseAnnulation,
  cautionHidden,
  mediateurNom,
  mediateurCoordonnees,
}) {
  const em = emitter || {};
  const l = log || {};
  const r = reservation || null;

  // Adresse loueur : même formule de concaténation que le bloc « Parties au
  // contrat » (adresse, CP ville) — cohérence en-tête ↔ clauses.
  const proprietaireAdresse = [
    em.adresse,
    [em.code_postal, em.ville].filter(Boolean).join(' '),
  ]
    .filter(Boolean)
    .join(', ');

  // Composition/capacité depuis log.capacity (mêmes champs que la carte
  // « Logement loué » : max_persons / bedrooms / bathrooms).
  const cap = l.capacity && typeof l.capacity === 'object' ? l.capacity : null;
  const composition = cap
    ? `${cap.bedrooms ?? '?'} chambre(s) · ${cap.bathrooms ?? '?'} salle(s) de bain`
    : BLANK_SHORT;
  const capacite = cap && cap.max_persons != null && cap.max_persons !== ''
    ? String(cap.max_persons)
    : BLANK_SHORT;

  // Montants : tarif_snapshot_breakdown (demande directe) quand disponible,
  // sinon repli total_amount pour le total, blancs pour le détail. Clés du
  // breakdown = celles persistées par api/sites.js (tarif_base, cleaning_fee,
  // taxe_sejour, total | total_avant_remise + promo_total_apres_eur).
  const bd = r && r.tarif_snapshot_breakdown && typeof r.tarif_snapshot_breakdown === 'object'
    ? r.tarif_snapshot_breakdown
    : null;
  const montantTotal = _fmtMontant(bd ? (bd.promo_total_apres_eur ?? bd.total ?? bd.total_avant_remise) : null)
    ?? _fmtMontant(r ? r.total_amount : null)
    ?? BLANK_AMOUNT;

  // Caution : même règle de validité que le récap historique (> 0 fini).
  const cautionNum = Number(cautionValue);
  const montantCaution = cautionValue != null && cautionValue !== '' && Number.isFinite(cautionNum) && cautionNum > 0
    ? cautionNum.toFixed(2)
    : BLANK_AMOUNT;

  // Drapeaux règles logement — booléen STRICT (cf. _contratRuleBoolStrict) :
  // seul un `false` EXPLICITE arme la clause d'interdiction. Mêmes sources cfg
  // que le catalogue emails Direct (allows_pets/animaux, allows_smoking/fumeurs).
  const allowsPets = _contratRuleBoolStrict(l, 'allows_pets', 'animaux');
  const allowsSmoking = _contratRuleBoolStrict(l, 'allows_smoking', 'fumeurs');

  // CONTRAT V2 — jumeaux caution. Verdict matrice : SEUL un cautionHidden===true
  // explicite bascule sur caution_absente (même doctrine « false explicite » que
  // cautionDue dans genContratHTML — undefined = rendu historique, caution due).
  // Mode empreinte/débit : nb de nuits RÉEL de la résa via la MÊME fonction que
  // la décision Stripe (cautionModeForNights, ADR-174) ; contrat type sans résa
  // ou dates invalides → 0 nuit → 'empreinte' (fail-safe le plus prudent).
  const cautionDueFlag = cautionHidden === true ? '' : '1';
  const nightsNum = Number(r ? _nightsCount(r.start_date, r.end_date) : '') || 0;
  const cautionSejour = cautionPourSejour({ figee: r || null, log: l, nuits: nightsNum });
  const cautionMode = cautionSejour.mode;

  // CONTRAT V2 — médiateur (art. 16) : chaînes nettoyées, '' si non configuré
  // (le paragraphe médiateur est alors masqué par son {#if}).
  const mediateurNomClean = _nonEmptyStr(mediateurNom) ? mediateurNom.trim() : '';
  const mediateurCoordClean = _nonEmptyStr(mediateurCoordonnees) ? mediateurCoordonnees.trim() : '';

  return {
    proprietaire_nom: em.nom || BLANK_LONG,
    proprietaire_adresse: proprietaireAdresse || BLANK_LONG,
    proprietaire_email: em.email || BLANK_LONG,
    proprietaire_telephone: em.telephone || BLANK_LONG,
    voyageur_nom: voyageurNom || BLANK_LONG,
    voyageur_email: voyageurEmail || BLANK_LONG,
    voyageur_telephone: voyageurTel || BLANK_LONG,
    // Nom PROPRE du logement, jamais l'identifiant technique (30/09) : sans
    // nom, le contrat est refusé en amont ; à défaut, un blanc — jamais « lc2 ».
    logement_nom: nomLogementFacture(l) || BLANK_LONG,
    logement_adresse: adressePublique || BLANK_LONG,
    logement_composition: composition,
    logement_capacite: capacite,
    date_arrivee: dateArrivee || BLANK_SHORT,
    date_depart: dateDepart || BLANK_SHORT,
    heure_arrivee: checkinTime || BLANK_SHORT,
    heure_depart: checkoutTime || BLANK_SHORT,
    nombre_nuits: (r ? _nightsCount(r.start_date, r.end_date) : '') || BLANK_SHORT,
    montant_hebergement: _fmtMontant(bd ? bd.tarif_base : null) ?? BLANK_AMOUNT,
    montant_menage: _fmtMontant(bd ? bd.cleaning_fee : null) ?? BLANK_AMOUNT,
    montant_taxe: _fmtMontant(bd ? bd.taxe_sejour : null) ?? BLANK_AMOUNT,
    montant_total: montantTotal,
    montant_caution: montantCaution,
    jours_avant_caution: String(JOURNEY_CAUTION_WINDOW_DAYS),
    // Pour l'instant = date d'émission du document.
    // TODO (lot consentement proprio) : remplacer par la date du consentement
    // explicite du loueur quand ce parcours existera.
    date_signature_loueur: dateEmission || BLANK_SHORT,
    // JAMAIS vide. Pré-résolue côté serveur (fail-closed en amont : clause non
    // résolue → pas de rendu du tout) ; absente (aperçu ADMIN client) →
    // placeholder d'avertissement VISIBLE, jamais un paragraphe deviné
    // (décision Dimitri 2026-08-01, cf. CONTRAT_CLAUSE_ANNULATION_PLACEHOLDER).
    clause_annulation: _nonEmptyStr(clauseAnnulation)
      ? clauseAnnulation
      : CONTRAT_CLAUSE_ANNULATION_PLACEHOLDER,
    seuil_nuits_caution: String(cautionSejour.seuil),
    mediateur_nom: mediateurNomClean,
    mediateur_coordonnees: mediateurCoordClean,
    animaux_non: allowsPets === false ? '1' : '',
    fumeur_non: allowsSmoking === false ? '1' : '',
    loueur_professionnel: em.isSousLoc ? '1' : '',
    caution_due: cautionDueFlag,
    caution_absente: cautionDueFlag ? '' : '1',
    caution_empreinte: cautionDueFlag && cautionMode === 'empreinte' ? '1' : '',
    caution_debit: cautionDueFlag && cautionMode === 'debit' ? '1' : '',
    nuits_connues: nightsNum > 0 ? '1' : '',
  };
}

// ── Moteur de rendu texte→texte des clauses ──────────────────────────────────
//
// (1) Blocs {#if flag}…{/if} via evaluateConditions (moteur partagé, résolveur
//     injecté = lecture de `vars` — vérité : valeur non vide après trim ; cumul
//     {#if A ET B} / {#if A OU B} supporté, mélange → masqué fail-safe).
// (2) UNE SEULE passe de remplacement sur tout candidat token `{…}` :
//       - nom \w connu du catalogue → sa valeur ;
//       - tout le reste (token inconnu, forme non-\w `{montant total}`,
//         marqueur {#if}/{/if} orphelin) → '' + console.error.
//     Passe UNIQUE volontaire : une VALEUR injectée (ex. politique custom
//     contenant des accolades) n'est JAMAIS re-scannée ni purgée.
//
// INVARIANT rétrocompat : un texte sans `{` ressort STRICTEMENT identique.
const CONTRAT_TOKEN_CANDIDATE_RE = /\{([^}\n]+)\}/g;
const CONTRAT_VAR_NAME_RE = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

function renderContratClauses(text, vars) {
  if (!text) return text == null ? '' : text;
  if (text.indexOf('{') === -1) return text; // fast-path : aucun token → byte-identique
  const v = vars || {};
  const resolver = (name) => (v[name] != null ? String(v[name]) : '');
  const conditioned = evaluateConditions(text, {}, resolver);
  return conditioned.replace(CONTRAT_TOKEN_CANDIDATE_RE, (_m, inner) => {
    if (CONTRAT_VAR_NAME_RE.test(inner) && Object.prototype.hasOwnProperty.call(v, inner)) {
      return String(v[inner]);
    }
    // 🔴 Jamais de token en clair dans un PDF signé : purge + trace (pas de PII).
    console.error(`[contrat] token inconnu {${inner}} dans les clauses — remplacé par une chaîne vide`);
    return '';
  });
}

// ── Validation au save (miroir lib/direct-messages-handlers.js) ──────────────

// Mêmes regex que la validation des gabarits Direct (IF_OPEN_RE / IF_CLOSE_RE) :
// EXPR = identifiant + espaces/tabs pour le cumul ET/OU.
const CONTRAT_IF_OPEN_RE = /\{#if\s+([a-zA-Z_][a-zA-Z0-9_ \t]*)\}/g;
const CONTRAT_IF_CLOSE_RE = /\{\/if\}/g;
const CONTRAT_IF_CUMUL_OPERATORS = new Set(['ET', 'OU']);

/**
 * Renvoie les {tokens} présents dans un texte de clauses mais INCONNUS du
 * catalogue contrat (CONTRAT_VAR_NAMES). Sert à REFUSER l'écriture (un contrat
 * ne doit jamais être signé avec une variable inexistante). Chaque opérande
 * d'un {#if …} est validé (ET/OU ne sont pas des variables) ; les marqueurs de
 * contrôle sont retirés avant le scan générique. Validation LARGE : tout `{…}`
 * mono-ligne est un candidat (les formes non-\w — accents, espaces, points —
 * ne seraient pas interpolées → refusées aussi).
 * @param {string} text
 * @returns {string[]} tokens inconnus uniques (ordre d'apparition)
 */
function unknownContratTokens(text) {
  if (typeof text !== 'string' || text.indexOf('{') === -1) return [];
  const declared = new Set(CONTRAT_VAR_NAMES);
  const found = [];
  const seen = new Set();
  const flag = (tok) => {
    if (!declared.has(tok) && !seen.has(tok)) {
      seen.add(tok);
      found.push(tok);
    }
  };
  CONTRAT_IF_OPEN_RE.lastIndex = 0;
  let mif;
  while ((mif = CONTRAT_IF_OPEN_RE.exec(text)) !== null) {
    const operands = String(mif[1])
      .split(/\s+/)
      .filter((t) => t && !CONTRAT_IF_CUMUL_OPERATORS.has(t));
    for (const op of operands) flag(op);
  }
  const scanned = text.replace(CONTRAT_IF_OPEN_RE, '').replace(CONTRAT_IF_CLOSE_RE, '');
  CONTRAT_TOKEN_CANDIDATE_RE.lastIndex = 0;
  let m;
  while ((m = CONTRAT_TOKEN_CANDIDATE_RE.exec(scanned)) !== null) {
    flag(m[1]);
  }
  return found;
}

// M1 (revue adversariale) — les marqueurs {#if …} / {/if} pour le scan
// séquentiel d'équilibre. MÊME forme d'ouverture que CONTRAT_IF_OPEN_RE (un
// {#if malformé au point de ne pas matcher — ex. {#if}, {#if 2x} — reste un
// candidat token du scan générique ci-dessus → déjà refusé comme inconnu).
const CONTRAT_IF_MARKER_RE = /\{#if\s+[a-zA-Z_][a-zA-Z0-9_ \t]*\}|\{\/if\}/g;

/**
 * M1 (revue adversariale) — problèmes STRUCTURELS des blocs {#if}…{/if} d'un
 * texte de clauses. Principe : la validation au save doit refuser EXACTEMENT
 * tout ce que le rendu (evaluateConditions + purge) ne sait pas traiter
 * proprement — sinon un {#if} non fermé rend son texte INCONDITIONNELLEMENT
 * (ex. « animaux interdits » dans le contrat d'un logement qui les accepte),
 * et une expression malformée masque en silence un texte juridique.
 * Refusés : (a) {#if} non fermé / {/if} orphelin / marqueurs dans le désordre ;
 * (b) expression hors de ce qu'evalIfExpression traite (variable seule, ou
 * cumul homogène `a ET b…` / `a OU b…` — mélange ET/OU interdit, opérateur en
 * position de variable interdit) ; (c) blocs imbriqués (non supportés).
 * @param {string} text
 * @returns {string[]} descriptions FR des problèmes ([] si structure saine)
 */
function malformedContratConditions(text) {
  if (typeof text !== 'string' || text.indexOf('{') === -1) return [];
  const errors = [];
  // (b) — chaque expression {#if …} doit être évaluable par le moteur.
  CONTRAT_IF_OPEN_RE.lastIndex = 0;
  let mo;
  while ((mo = CONTRAT_IF_OPEN_RE.exec(text)) !== null) {
    const expr = String(mo[1]).trim();
    const tokens = expr.split(/\s+/).filter(Boolean);
    const operands = tokens.filter((_, i) => i % 2 === 0);
    const operators = tokens.filter((_, i) => i % 2 === 1);
    const homogeneous =
      operators.every((o) => o === 'ET') || operators.every((o) => o === 'OU');
    const valid =
      tokens.length > 0
      && tokens.length % 2 === 1
      && homogeneous
      && operators.every((o) => CONTRAT_IF_CUMUL_OPERATORS.has(o))
      && operands.every((v) => !CONTRAT_IF_CUMUL_OPERATORS.has(v));
    if (!valid) {
      errors.push(
        `expression invalide « {#if ${expr}} » (formes acceptées : {#if variable}, {#if a ET b}, {#if a OU b} — sans mélange ET/OU)`,
      );
    }
  }
  // (a) + (c) — équilibre ET ordre des marqueurs (profondeur séquentielle).
  CONTRAT_IF_MARKER_RE.lastIndex = 0;
  let depth = 0;
  const markers = text.match(CONTRAT_IF_MARKER_RE) || [];
  for (const marker of markers) {
    if (marker === '{/if}') {
      if (depth === 0) errors.push('{/if} orphelin (aucun {#if} ouvert avant)');
      else depth -= 1;
    } else {
      if (depth > 0) errors.push('blocs {#if} imbriqués non supportés');
      depth += 1;
    }
  }
  if (depth > 0) {
    errors.push(`{#if} non fermé (il manque ${depth} × {/if})`);
  }
  return errors;
}

export {
  CONTRAT_CLAUSE_ANNULATION_TEXTS,
  CONTRAT_CLAUSE_ANNULATION_PLACEHOLDER,
  CONTRAT_POLICY_ENUMS,
  CONTRAT_TOKEN_NAMES,
  CONTRAT_FLAG_NAMES,
  CONTRAT_VAR_NAMES,
  resolveContratClauseAnnulation,
  formatSignatureDateTimeFr,
  buildContratVars,
  renderContratClauses,
  unknownContratTokens,
  malformedContratConditions,
};
